import React, { useEffect, useRef, useCallback } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SESSION_LAST_ACTIVITY_KEY, SESSION_LOGOUT_SIGNAL_KEY } from "@/utility/sessionStorageKeys";

interface SessionLoggedOutModalProps {
    isOpen: boolean;
    onCancel: () => void;
}

export const SessionLoggedOutModal: React.FC<SessionLoggedOutModalProps> = ({
    isOpen,
    onCancel,
}) => {
    const { t } = useTranslation();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Auto-close after 5 minutes
    useEffect(() => {
        if (isOpen) {
            timeoutRef.current = setTimeout(() => {
                onCancel();
            }, 5 * 60 * 1000);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isOpen, onCancel]);

    // Clear session keys when modal closes
    useEffect(() => {
        if (!isOpen) {
            localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
            localStorage.removeItem(SESSION_LOGOUT_SIGNAL_KEY);
        }
    }, [isOpen]);

    const handleClose = useCallback(() => {
        localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
        localStorage.removeItem(SESSION_LOGOUT_SIGNAL_KEY);
        onCancel();
    }, [onCancel]);

    // Improved keyboard handling
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === "Escape") {
            e.preventDefault();
            handleClose();
        }
    }, [handleClose]);

    if (!isOpen) {
        return null;
    }

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            aria-labelledby="session-logged-out-title"
            aria-describedby="session-logged-out-description"
            maxWidth="sm"
            fullWidth
            onKeyDown={handleKeyDown}
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: "background.paper",
                    }
                }
            }}
        >
            <DialogTitle
                id="session-logged-out-title"
                sx={{
                    color: "error.main",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "1.5rem"
                }}
            >
                {t("sessionTimeout.loggedOut.title")}
            </DialogTitle>

            <DialogContent>
                <DialogContentText
                    id="session-logged-out-description"
                    sx={{
                        color: "text.primary",
                        textAlign: "center",
                        fontSize: "1.1rem"
                    }}
                >
                    {t("sessionTimeout.loggedOut.message")}
                </DialogContentText>
            </DialogContent>

            <DialogActions sx={{ padding: "16px 24px", px: 3, py: 2 }}>
                <Button
                    onClick={handleClose}
                    variant="contained"
                    disableElevation
                    disableRipple
                    autoFocus
                    data-testid="session-logged-out-cancel-button"
                    sx={{
                        fontWeight: "bold",
                        backgroundColor: "error.main",
                        color: "error.contrastText",
                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                        "&:hover": {
                            backgroundColor: "error.dark",
                            color: "error.contrastText",
                        },
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                        "&:focus-visible": {
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.error.main}40`,
                        },
                        minWidth: "120px",
                    }}
                    aria-describedby="session-logged-out-description"
                >
                    {t("sessionTimeout.actions.cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
